import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { useTranslation } from "react-i18next";
import Get from "../../Utils/API/Get";
import userContext from "../../../context/user-context";
import InvoiceTable from "../Table/InvoiceTable";
import DateFormated from "../../Utils/Date/Date";
import Auxiliary from "../../../hoc/Auxiliary";
import axios from "../../../axios-defaults";
import formatDate from "../../Utils/Date/formatDate";
import {TODAY, TODAY_MINUS_14, TODAY_MINUS_30, DEFAULT_FILTER_ALL_PAGE_NUMBER, DEFAULT_FILTER_ALL_PAGE_SIZE} from "../../Accounts/Account/Tables/DateFilters";
import {fireGTMDownloadFileEvent} from "../../Utils/GTM/GTMHelper";

const getStatements = async ({ startDate, endDate, pageNumber, pageSize}) => {
    let queryJson = {};
    if (pageNumber && pageSize) {
        queryJson = {
            pageNumber,
            pageSize,
            communicationTypeCode:'R'
        };
    } else {
        queryJson = {
            startDate,
            endDate,
            communicationTypeCode:'R'
        };
    }

    try {
        const statements = await Get({
            endpoint: '/accounts/communications',
            queryJson,
        });
        if (!statements) {
            return false;
        }
        return statements;
    } catch (e) {
        console.error(e.message);
        return false;
    }
};
const getDocumentsStatus = async () => {
    const queryJson = {};
    try {
        const documents = await Get({
            endpoint: '/documents_status',
            queryJson,
        });
        if (!documents) {
            return false;
        }
        return documents;
    } catch (e) {
        console.error(e.message);
        return false;
    }
};

const mapStatement = (statement, documentsStatus) => {
    let docStatus;
    if (documentsStatus) {
        docStatus = documentsStatus.find((data) => {
            return data['document_path'] == statement.documentPath;
        })
    }
    return {
        ...statement,
        accountName: {name:statement.subAccountName,code:statement.subAccountCode},
        status: !docStatus?'U':(docStatus.document_status),
    };
};

const Statements = () => {

    const [totalRecords, setTotalRecords] = useState(0);
    const { user } = useContext(userContext);
    const { t, i18n } = useTranslation();
    const [statements, setStatements] = useState([]);
    const [pages, setPages] = useState(0);
    const [loading, setLoading] = useState(true);
    const [startDate, setStartDate] = useState(formatDate(new Date(TODAY_MINUS_14)));
    const [endDate, setEndDate] = useState(formatDate(TODAY));
    const [pageNumber, setPageNumber] = useState(DEFAULT_FILTER_ALL_PAGE_NUMBER);
    const [pageSize, setPageSize] = useState(DEFAULT_FILTER_ALL_PAGE_SIZE);
    const [isSpecific, setIsSpecific] = useState(false);
    const [dateFilterType, setDateFilterType] = useState('last_all');
    const [pageIndex, setPageIndex] = useState(0);
	const [searchData, setSearchData] = useState([]);

    let items;
    let documentsStatus;

    /* START DATEFILTERS FUNCTIONS */
    const dateFilterSetStartDate = (date) => {
        if (date != null) {
            const dateFormatted = `${date.toISOString().split("T")[0]}T00:00:00.000Z`;
            setStartDate(dateFormatted)
        }
    }
    const dateFilterSetEndDate = (date) => {
        if(date != null){
            const dateFormatted = `${date.toISOString().split("T")[0]}T23:59:59.999Z`;
            setEndDate(dateFormatted)
        }
    }
    const setLastAll = (refresh = true, component = null) => {
        setStartDate(null);
        setEndDate(null);
        setPageNumber(DEFAULT_FILTER_ALL_PAGE_NUMBER);
        setPageSize(DEFAULT_FILTER_ALL_PAGE_SIZE);
        setIsSpecific(false);
    }
    const setLast14Days = (refresh = true, component = null) => {
        setStartDate(formatDate(new Date(TODAY_MINUS_14)));
        setEndDate(formatDate(TODAY,'23:59:59.999Z'));
        setPageNumber(null);
        setPageSize(null);
        setIsSpecific(false);
    }
    const setLast30Days = () => {
        setStartDate(formatDate(new Date(TODAY_MINUS_30)));
        setEndDate(formatDate(TODAY,'23:59:59.999Z'));
        setPageNumber(null);
        setPageSize(null);
        setIsSpecific(false);
    }
    const setSpecificDate = () => {
        setStartDate(null);
        setEndDate(null);
        setPageNumber(null);
        setPageSize(null);
        setIsSpecific(true);
    }
    const dateFilterSetDateFilterType = (type) => {
        setDateFilterType(type)
        switch (type) {
            case 'last_all': setLastAll(); break;
            case 'last_14': setLast14Days(); break;
            case 'last_30': setLast30Days(); break;
            case 'specific_date': setSpecificDate(); break;
            default: setLastAll();
        }
    }
    /* END DATEFILTERS FUNCTIONS */

    useEffect(() => {
        if(isSpecific && (startDate === null || endDate === null)){
            return null;
        }else{
            setLoading(true);
        }
        if (user) {
            getDocumentsStatus().then((documents) => {
                documentsStatus = documents.data;
            });
            getStatements({
                startDate:startDate,
                endDate:endDate,
                pageNumber: pageNumber,
                pageSize: pageSize
            }).then((datas) => {
                const mappedStatements = datas.data.map((statement) =>
                    mapStatement(statement, documentsStatus)
                );
                setStatements(mappedStatements);
                items = mappedStatements;
                setPages(datas.totalPages);
                setLoading(false);
                setTotalRecords(datas.totalRecords);
            })
        }
    }, [user, startDate, endDate,dateFilterType]);

    const storeSortedItems = (items) => {
        return setStatements(items);
    }
    const handlePage = (pageIndex) => {
        setPageIndex(pageIndex);
    }
    const onClickAction = (filename) => {
        axios.get(`/documents/download?filename=${encodeURI(filename)}`)
            .then(response => {
                if (response.data.succeeded) {
                    const newItems = items;
                    let key = 0;
                    const editItem = items.find((item, index) => {
                        key = index;
                        return item.documentPath == filename;
                    });
                    editItem.status = 'R';
                    newItems[key] = editItem;
                    setStatements(Object.values(newItems));
					fireGTMDownloadFileEvent('releve','PDF');
                }
            }).catch(error => {
            console.log('[Statements/index.js] : DocumentStatus ajax error in onClickAction()',error.message);
        });
        window.open(`/rest/v1/communications/download?filename=${encodeURI(filename)}`)
    }
    const columns = useMemo(
        () => [
            {
                Header: t("statements.table.headers.date"),
                accessor: 'communicationDate',
                sort: 'desc',
                Cell: ({cell}) => {
                    const {value} = cell;
                    return value ? <DateFormated date={value}/> : '';
                }
            },
            {
                Header: t("statements.table.headers.status"),
                accessor: 'status',
                Cell: ({cell}) => {
                    const {value} = cell;
                    const pickStatus = (value) => {
                        let status = '';
                        if (value === 'R') {
                            status = (
                                <div className="status status--black">
                                    <i className="fas fa-eye"></i>&nbsp;{t('statements.table.read')}
                                </div>
                            );
                        } else {
                            status = (
                                <div className="status status--red">
                                    <i className="fas fa-eye-slash"></i>&nbsp;{t('statements.table.unread')}
                                </div>
                            );
                        }
                        return status;
                    };
                    return (<div>{pickStatus(value)}</div>);
                }
            },
            {
                id: 'documentPath',
                accessor: 'documentPath',
                Cell: ({cell}) => {
                    const {value} = cell;
                    if (value) {
                        return (
                                <a href="#"
                                   onClick={(e) => {
                                       e.preventDefault();
                                       onClickAction(value);
                                   }}
                                   className="link link--red link--upper link--accent link--fs-14">
                                    <strong>{t('statements.table.downloadpdf')}&nbsp;&nbsp;<i className="fas fa-angle-right"></i></strong>
                                </a>
                            );
                    } else {
                        return '';
                    }
                }
            },
        ],
        [t, i18n]
    );

    let sorterValues = [
        {
            label: t("statements.table.headers.date"),
            id: 'communicationDate',
            value: 'communicationDate',
            type: 'date'
        },{
            label: t("statements.table.headers.status"),
            id: 'status',
            value: 'status',
            type: 'int'
        },
    ];

    return (
        <div className="documents">
            <div className="container container--larger flex nopadding">
                <h2 className="headline headline--section">
                    {t("statements.title")}
                </h2>
                <div className="row">
                    <div className="col-12"></div>
                </div>
            </div>
            <InvoiceTable
                columns={columns}
                data={statements}
                pageCount={pages}
                totalRecords={totalRecords}
                loading={loading}
                startDate={startDate}
                setStartDate={dateFilterSetStartDate}
                endDate={endDate}
                setEndDate={dateFilterSetEndDate}
                accountTypes={[]}
                getSortedItems={storeSortedItems}
                sorterValues={sorterValues}
                initialSortField='communicationDate'
                onPage={handlePage}
                setDateFilterType={dateFilterSetDateFilterType}
                dateFilterType={dateFilterType}
                initialPageIndex={pageIndex}
                labelFilterAll={t("docs.invoices.table.filter.all.statement")}
				searchData={searchData}
				setSearchData={setSearchData}

            />
        </div>
    );
};

export default Statements;
